import { useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { updateAccount } from 'api/account';
import * as IntegrationItem from 'assets/js/pages/account-settings/account-integrations/IntegrationItem';
import * as Form from 'components/form';
import { Settings } from 'constants/settings';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentAccountSettings } from 'customHooks/useCurrentAccountSettings';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface GoogleAnalyticsFormValues {
    apiKey: string;
}

const GoogleAnalytics = () => {
    const { accountId } = useCurrentAccount();
    const { success, error } = useToast();
    const data = useCurrentAccountSettings();
    const { active, value, name, type } = data?.googleAnalytics || {};

    const methods = Form.useGivebutterForm<GoogleAnalyticsFormValues>({
        defaultValues: { apiKey: active ? value || '' : '' },
    });
    const { control, path, handleSubmit } = methods;

    const { mutate: submit, isPending: isLoading } = useMutation<
        unknown,
        GivebutterAxiosError,
        GoogleAnalyticsFormValues
    >({
        mutationFn: ({ apiKey }) => {
            return updateAccount(accountId, {
                settings: [
                    {
                        value: apiKey,
                        code: Settings.GoogleAnalytics.Code,
                        active: apiKey ? apiKey.trim().length > 0 : false,
                        name: name ?? '',
                        type: type ?? 'text',
                    },
                ],
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.current(accountId) });
            success('Updated Google Analytics successfully!');
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Something went wrong. Please try again.');
        },
    });

    return (
        <IntegrationItem.Root>
            <IntegrationItem.Header>
                <IntegrationItem.Logo>
                    <img
                        alt="Google Analytics"
                        className="max-w-[70px]"
                        src={`/images/logos/googleanalytics.png`}
                    />
                </IntegrationItem.Logo>

                <IntegrationItem.Description>
                    <p className="text-xl font-medium">Google Analytics</p>

                    <p className="max-w-xs text-base text-gray-700">
                        Track visitor activity and conversion rates on your Givebutter campaigns.
                    </p>

                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://analytics.google.com"
                    >
                        https://analytics.google.com
                    </a>
                </IntegrationItem.Description>

                <IntegrationItem.Expander />
            </IntegrationItem.Header>
            <IntegrationItem.Content>
                <Form.Provider {...methods}>
                    <form
                        onSubmit={handleSubmit(values => submit(values))}
                        className="flex flex-col items-start gap-4"
                    >
                        <Form.Container>
                            <Form.Label name={path('apiKey')} className="text-base">
                                Google Analytics Tracking ID
                            </Form.Label>
                            <p className="text-sm">Leave empty to turn off integration</p>
                            <Form.TextInputRoot name={path('apiKey')} control={control}>
                                <Form.TextInputForm placeholder="Google Analytics Tracking ID (starts with UA- or G-)" />
                            </Form.TextInputRoot>
                        </Form.Container>

                        <Form.SubmitButton isSubmitting={isLoading}>Save</Form.SubmitButton>
                    </form>
                </Form.Provider>
            </IntegrationItem.Content>
        </IntegrationItem.Root>
    );
};

export { GoogleAnalytics };
